import React, { useState } from 'react';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { sanitizeFilename } from '../../scripts/document.filename.script';
import { Company } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Invoice } from '../../models/invoice.model';
import { InvoiceGroup } from '../../models/invoice.group.model';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { dateFormat } from '../../scripts/date.time.format.script';
import { accumulatedAmount } from '../../scripts/financial.calculation.script';
import { nameGroup } from '../../libraries/group.name.library';
import { formatContact } from '../../scripts/format.value.script';
import { validityBoolean } from '../../libraries/validation.library';
import { normalizeStatus, statusDocumentValue } from '../../libraries/status.document.library';
import { paymentStatusValue } from '../../libraries/payment.library';
import { InvoiceDA } from '../../services/admin/invoice.service';

import AppInputExportType from '../element/InputExportType';
import logo from '../../assets/themes/svg/logo-official.png';

export interface AppModalInvoiceExportProps {
  companyForUser: Company | null,
  grouped: {state: boolean, desc: string},
  invoices: Invoice[] | null,
  invoiceGroups: InvoiceGroup[] | null,
  filter?: {interval: string, start_date: string, end_date: string, contact: string, validity: string, status: string, expiration: string, payment: string, sort_field: string, sort_mode: string, group: string, search: string},
  vatSubject?: boolean
};

const AppModalInvoiceExport: React.FunctionComponent<AppModalInvoiceExportProps> = ({companyForUser, grouped, invoices, invoiceGroups, filter, vatSubject}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Invoices Data')
    const lastCol = vatSubject ? 'L' : 'K'

    worksheet.addRow([])
    worksheet.mergeCells(`A1:${lastCol}1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logo, {responseType: 'arraybuffer'})

    let imageLogotipo = workbook.addImage({
      buffer: imageBuffer.data,
      extension: 'png'
    })

    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.companyListOfInvoices}`])
    titleRow.font = {size: 18, bold: true}
    titleRow.getCell(1).alignment = {vertical:'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${titleRow.number}:${lastCol}${titleRow.number}`)
    worksheet.getRow(2).height = 30

    worksheet.addRow([])
    worksheet.mergeCells(`A3:${lastCol}3`)
    worksheet.getRow(3).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell( (cell) => {
      cell.alignment = {vertical:'distributed', horizontal: 'left'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })

    worksheet.getRow(4).height = 20

    tableData.forEach((item) => {
      let bodyRow = worksheet.addRow(item)

      if (grouped.state) {
        if (item.length === 1) {
          bodyRow.font = { bold: true }
        }
      }
    })

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          worksheet.mergeCells(`A${i + 5}:${lastCol}${i + 5}`)
        }
      }
    }

    worksheet.getColumn(1).width = 18;
    worksheet.getColumn(2).width = 30;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 13;
    worksheet.getColumn(5).width = 13;
    worksheet.getColumn(6).width = 13;
    worksheet.getColumn(7).width = 13;
    worksheet.getColumn(8).width = 13;
    worksheet.getColumn(9).width = 17;
    worksheet.getColumn(10).width = 15;
    worksheet.getColumn(11).width = 15;
    if (vatSubject) { worksheet.getColumn(12).width = 15; }

    workbook.xlsx.writeBuffer().then( data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Invoices_of_${company.name.replaceAll(' ', '_')}.xlsx`);
    })
  }

  const filePdf = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    const rightAlignIndices = vatSubject ? [8, 9, 10, 11] : [8, 9, 10]

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          tableData[i][0] = {colSpan: vatSubject ? 10 : 9, content: tableData[i][0], styles: {fontStyle: 'bold'}}
        } else {
          for (const idx of rightAlignIndices) {
            tableData[i][idx] = {content: tableData[i][idx], styles: {halign: 'right'}}
          }
        }
      }
    } else {
      for (let i = 0; i < tableData.length; i++) {
        for (const idx of rightAlignIndices) {
          tableData[i][idx] = {content: tableData[i][idx], styles: {halign: 'right'}}
        }
      }
    }

    document.addImage(logo, 'PNG', 290, 5, 30, 9)

    document.setFontSize(18)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.companyListOfInvoices}`, 25, 20)

    autoTable(document, {head: tableTitle, body: tableData, startY: 25})

    document.save(`Invoices_of_${sanitizeFilename(company.name).replaceAll(' ', '_')}.pdf`)
  }

  const buildTableData = (items: Invoice[]) => {
    const tableData: any[][] = []
    for (let invoice of items) {
      tableData.push([invoice.invoice_number, invoice.concerning, formatContact(invoice.customer), validityBoolean(lang, invoice.validated), statusDocumentValue(lang, invoice.status), paymentStatusValue(lang, invoice.payment_status), dateFormat(lang.code, invoice.valid_until), `${invoice.currency.name} (${invoice.currency.symbol})`, ...(vatSubject ? [Number((invoice.amount.tax_total ?? 0).toFixed(2))] : []), Number(invoice.amount.total.toFixed(2)), Number(invoice.amount.paid.toFixed(2)), Number(invoice.amount.debt.toFixed(2))])
    }
    tableData.push(['', '', '', '', '', '', '', '', ...(vatSubject ? [Number(accumulatedAmount(items).tax.toFixed(2))] : []), Number(accumulatedAmount(items).total.toFixed(2)), Number(accumulatedAmount(items).paid.toFixed(2)), Number(accumulatedAmount(items).debt.toFixed(2))])
    return tableData
  }

  const executeInvoiceExport = async () => {
    setLoadIndicator('on')

    if (companyForUser && invoices && invoiceGroups && formatExport.valid) {
      let tableTitle: string[][] = [[lang.labels.invoiceNumber.toUpperCase(), lang.labels.concerning.toUpperCase(), lang.labels.customer.toUpperCase(), lang.labels.validity.toUpperCase(), lang.labels.status.toUpperCase(), lang.labels.payment.toUpperCase(), lang.labels.expiration.toUpperCase(), lang.labels.currency.toUpperCase(), ...(vatSubject ? [lang.labels.vatAmount.toUpperCase()] : []), lang.labels.amountTotal.toUpperCase(), lang.labels.amountPaid.toUpperCase(), lang.labels.amountDebt.toUpperCase()]]
      let tableData: any[][] = []

      if (grouped.state) {
        for (let i = 0; i < invoiceGroups.length; i++) {
          tableData.push([`GROUP ${i + 1}: ${nameGroup(lang, grouped.desc, invoiceGroups[i].group_detail).toUpperCase()}`])
          for (let invoice of invoiceGroups[i].invoices) {
            tableData.push([invoice.invoice_number, invoice.concerning, formatContact(invoice.customer), validityBoolean(lang, invoice.validated), statusDocumentValue(lang, invoice.status), paymentStatusValue(lang, invoice.payment_status), dateFormat(lang.code, invoice.valid_until), `${invoice.currency.name} (${invoice.currency.symbol})`, ...(vatSubject ? [Number((invoice.amount.tax_total ?? 0).toFixed(2))] : []), Number(invoice.amount.total.toFixed(2)), Number(invoice.amount.paid.toFixed(2)), Number(invoice.amount.debt.toFixed(2))])
          }
          tableData.push(['', '', '', '', '', '', '', '', ...(vatSubject ? [Number(accumulatedAmount(invoiceGroups[i].invoices).tax.toFixed(2))] : []), Number(accumulatedAmount(invoiceGroups[i].invoices).total.toFixed(2)), Number(accumulatedAmount(invoiceGroups[i].invoices).paid.toFixed(2)), Number(accumulatedAmount(invoiceGroups[i].invoices).debt.toFixed(2))])
        }
      } else if (filter) {
        const response = await InvoiceDA.getInvoices(companyForUser.id, filter.interval, filter.start_date, filter.end_date, filter.contact, filter.validity, filter.status, filter.expiration, filter.payment, filter.sort_field, filter.sort_mode, '', 1, 20, filter.search, true)
        if (response.status === 200) {
          const allItems = (response.data.items ?? response.data.invoices ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) }))
          tableData = buildTableData(allItems)
        } else {
          tableData = buildTableData(invoices)
        }
      } else {
        tableData = buildTableData(invoices)
      }

      setTimeout( async () => {
        switch (formatExport.value) {
          case 'excel':
            fileExcel(companyForUser, tableTitle, tableData)
            break;
          case  'pdf':
            filePdf(companyForUser, tableTitle, tableData)
            break;
        }

        setLoadIndicator('off')
        executeHideModalInvoiceExport()
      }, 1000)
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        legendInvalid('modal-invoice-export-legend-format-export-type-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalInvoiceExport = () => {
    modalHide('modal-invoice-export')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-invoice-export-legend-format-export-type-required')
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id="modal-invoice-export" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportInvoices}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={"modal-invoice-export"} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalInvoiceExport}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeInvoiceExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalInvoiceExport;
